import React, { useState } from 'react';
import { Code, Copy, Trash2, Check, X } from 'lucide-react';

const CssSelectorTesterTool = () => {
  const [html, setHtml] = useState('');
  const [selector, setSelector] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const testSelector = () => {
    setError('');
    setResults(null);

    if (!html.trim()) {
      setError('Please enter HTML to test against');
      return;
    }

    if (!selector.trim()) {
      setError('Please enter a CSS selector');
      return;
    }

    try {
      // Create a temporary DOM element
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Query the selector
      const elements = doc.querySelectorAll(selector);
      
      // Extract matching elements info
      const matches = Array.from(elements).map((el, i) => ({
        index: i,
        tag: el.tagName.toLowerCase(),
        id: el.id || null,
        classes: Array.from(el.classList),
        text: el.textContent?.trim().substring(0, 100) || '',
        attributes: Array.from(el.attributes)
          .filter(a => !['id', 'class'].includes(a.name))
          .map(a => ({ name: a.name, value: a.value })),
        outerHTML: el.outerHTML.substring(0, 200)
      }));

      setResults({
        count: matches.length,
        matches
      });
    } catch (e) {
      setError(`Invalid selector: ${e.message}`);
    }
  };

  const loadSample = () => {
    const sampleHtml = `<div class="container">
  <header id="main-header" class="header primary">
    <nav class="nav">
      <a href="/" class="nav-link active">Home</a>
      <a href="/about" class="nav-link">About</a>
      <a href="/contact" class="nav-link">Contact</a>
    </nav>
  </header>
  <main>
    <article class="post" data-id="123">
      <h1 class="title">First Post</h1>
      <p class="content">Hello world!</p>
    </article>
    <article class="post featured" data-id="456">
      <h1 class="title">Featured Post</h1>
      <p class="content">This is featured.</p>
    </article>
  </main>
  <footer class="footer">
    <p>&copy; 2024</p>
  </footer>
</div>`;
    setHtml(sampleHtml);
    setSelector('.nav-link');
  };

  const selectorExamples = [
    { selector: '.nav-link', desc: 'Class selector' },
    { selector: '#main-header', desc: 'ID selector' },
    { selector: 'article.post', desc: 'Element with class' },
    { selector: '.post.featured', desc: 'Multiple classes' },
    { selector: '[data-id]', desc: 'Has attribute' },
    { selector: '[data-id="123"]', desc: 'Attribute value' },
    { selector: '.nav-link:first-child', desc: 'First child' },
    { selector: '.post > h1', desc: 'Direct child' },
    { selector: 'header nav a', desc: 'Descendant' },
    { selector: '.nav-link + .nav-link', desc: 'Adjacent sibling' },
  ];

  const copySelector = () => {
    navigator.clipboard.writeText(selector);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-300">HTML</label>
            <button
              onClick={loadSample}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Load Sample
            </button>
          </div>
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            placeholder="<div class='container'>...</div>"
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Results</label>
          <div className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 overflow-auto">
            {error ? (
              <div className="flex items-center gap-2 text-red-400">
                <X size={18} />
                {error}
              </div>
            ) : results ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-lg">
                  {results.count > 0 ? (
                    <Check className="text-green-400" size={20} />
                  ) : (
                    <X className="text-red-400" size={20} />
                  )}
                  <span className={results.count > 0 ? 'text-green-400' : 'text-red-400'}>
                    {results.count} element{results.count !== 1 ? 's' : ''} matched
                  </span>
                </div>
                {results.matches.map((match, i) => (
                  <div key={i} className="p-3 bg-gray-900 rounded-lg text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-purple-400">&lt;{match.tag}&gt;</span>
                      {match.id && <span className="text-blue-400">#{match.id}</span>}
                      {match.classes.map(c => (
                        <span key={c} className="text-green-400">.{c}</span>
                      ))}
                    </div>
                    {match.text && (
                      <div className="text-gray-400 truncate">Text: {match.text}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-gray-500 text-sm">Results will appear here...</span>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">CSS Selector</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={selector}
            onChange={(e) => setSelector(e.target.value)}
            placeholder=".class, #id, [attribute], etc."
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white font-mono placeholder-gray-500 focus:border-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && testSelector()}
          />
          <button
            onClick={testSelector}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Code size={18} />
            Test
          </button>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Selector Examples</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {selectorExamples.map((ex) => (
            <button
              key={ex.selector}
              onClick={() => setSelector(ex.selector)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm text-left transition-colors"
              title={ex.desc}
            >
              <code className="text-green-400">{ex.selector}</code>
              <div className="text-xs text-gray-500 mt-1">{ex.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={copySelector}
          disabled={!selector}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Selector'}
        </button>
        <button
          onClick={() => { setHtml(''); setSelector(''); setResults(null); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear All
        </button>
      </div>
    </div>
  );
};

export default CssSelectorTesterTool;
